import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("btw_token") || null);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  // axios global default — every admin call sends Bearer + credentials
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const url = config.url || "";
      const isAdminCall = url.includes("/api/auth/") || url.includes("/api/admin/");
      if (token && url.includes("/api/")) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      // Only send cookies/credentials for admin/auth endpoints. Public
      // customer endpoints (checkout, pickup menu, newsletter) must not send
      // credentials — otherwise the browser rejects CORS responses that use
      // `Access-Control-Allow-Origin: *` (per the CORS spec, wildcard + creds
      // is invalid), which manifests as ERR_NETWORK on the customer's device.
      config.withCredentials = isAdminCall;
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, [token]);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch (e) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      refreshMe();
    } else {
      setLoading(false);
    }
  }, [token, refreshMe]);

  const loginPassword = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("btw_token", data.session_token);
    setToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const loginGoogle = () => {
    const redirect = `${window.location.origin}/admin/auth-callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const exchangeGoogleSession = async (sessionId) => {
    const { data } = await axios.post(`${API}/auth/session`, { session_id: sessionId });
    localStorage.setItem("btw_token", data.session_token);
    setToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Logout API failures are non-blocking — we still clear local state.
      console.warn("[auth] server logout failed (clearing local state anyway)", err);
    }
    localStorage.removeItem("btw_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthCtx.Provider
      value={{ user, token, loading, loginPassword, loginGoogle, exchangeGoogleSession, logout, refreshMe, authHeaders }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
